import { defineStore } from 'pinia';
import { ref } from 'vue';

import type {
  IApiResponseAllCommerceSlugs,
  IApiResponseCommerceSlugs,
  IApiResponseRestaurant,
  ICommerceBodySlug,
  ICommerceBodyUpdate,
  ICommerceLogo,
  ICommerceSlug,
  IContact,
  IAddStoreContactPayload,
} from '@/types/commerce.types';
import { CommerceService } from '@/services/commerce.services';
import type { ICommerceBody } from '@/types/commerce.types';
import { idToAddValue } from '@/refs/commerce.refs';
import type { AxiosResponse } from 'axios';

/**
 * Store pour gérer les commerces et leurs actions associées
 */
export const useCommerceStore = defineStore('commerce', () => {
  /**
   * Crée un nouveau commerce (login ou enregistrement selon le contexte)
   * @param {ICommerceBody} data Données du commerce à créer
   * @returns {Promise<any>} Réponse de l'API
   */
  const createCommerce = async (data: ICommerceBody): Promise<any> => {
    try {
      const response = await CommerceService.createCommerce(data);

      idToAddValue.value = response?.data.data?.id;
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Crée un nouveau commerce conatct (login ou enregistrement selon le contexte)
   * @param {IContact} data Données du commerce contact à créer
   * @returns {Promise<any>} Réponse de l'API
   */
  const createCommerceContact = async (data: IContact): Promise<any> => {
    try {
      const response = await CommerceService.createCommerceContact(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Crée un nouveau commerce conatct (login ou enregistrement selon le contexte)
   * @param {ICommerceLogo} data Données du commerce contact à créer
   * @returns {Promise<any>} Réponse de l'API
   */
  const createCommerceLogo = async (data: ICommerceLogo): Promise<any> => {
    try {
      const response = await CommerceService.createCommerceLogo(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * État réactif contenant les commerces récupérés
   * @type {Ref<IApiResponseRestaurant | null>}
   */
  const dataGetCommerces = ref<IApiResponseRestaurant | null>(null);

  /**
   * Récupère les commerces liés à l'utilisateur connecté
   * @returns {Promise<IApiResponseRestaurant>} Les données des commerces
   */
  const getCommerces = async (): Promise<IApiResponseRestaurant> => {
    const response = await CommerceService.getCommerces();
    dataGetCommerces.value = response.data;
    return response.data;
  };

  /**
   * Met à jour les informations d’un commerce existant.
   * @param data - Données du commerce à mettre à jour (ICommerceBody)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  const UpdateCommerce = async (data: ICommerceBodyUpdate) => {
    try {
      const response = await CommerceService.UpdateCommerce(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
  /**
   * Met à jour les informations d’un commerce existant.
   * @param data - Données du commerce à mettre à jour (ICommerceBody)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  const UpdateCommerceContact = async (data: any) => {
    try {
      const response = await CommerceService.UpdateCommerceContact(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Suprimer commerce conatct (login ou enregistrement selon le contexte)
   * @param {restaurantId} data Données du commerce contact à créer
   * @returns {Promise<any>} Réponse de l'API
   */
  const DeleteCommerce = async (restaurantId: string): Promise<any> => {
    try {
      const response = await CommerceService.DeleteCommerce(restaurantId);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Suprimer commerce conatct (login ou enregistrement selon le contexte)
   * @param {restaurantId} data Données du commerce contact à créer
   * @returns {Promise<any>} Réponse de l'API
   */
  const DeleteCommerceContact = async (): Promise<any> => {
    try {
      const response = await CommerceService.DeleteCommerceContact();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Crée un slug pour un commerce existant.
   *
   * Cette fonction appelle le service de création de slug
   * afin d’associer un identifiant lisible (slug) à un commerce.
   *
   * @async
   * @function createCommerceSlug
   * @param {ICommerceBodySlug} data - Données nécessaires à la création du slug.
   * @returns {Promise<AxiosResponse<IApiResponseCommerceSlugs>>}
   * Une Promise qui résout avec la réponse de l’API,
   * ou rejette avec une erreur en cas d’échec.
   */
  const createCommerceSlug = async (
    data: ICommerceBodySlug,
  ): Promise<AxiosResponse<IApiResponseCommerceSlugs>> => {
    try {
      const response = await CommerceService.createCommerceSlug(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Récupère la liste des slugs associés à un commerce.
   *
   * Cette fonction appelle le service afin d’obtenir
   * tous les slugs liés à un commerce donné.
   *
   * @async
   * @function getCommerceSlugs
   * @returns {Promise<AxiosResponse<IApiResponseAllCommerceSlugs>>}
   * Une Promise qui résout avec la réponse de l’API contenant
   * la liste des slugs, ou rejette avec une erreur en cas d’échec.
   */
  const getCommerceSlugs = async (): Promise<
    AxiosResponse<IApiResponseAllCommerceSlugs>
  > => {
    try {
      const response = await CommerceService.getCommerceSlugs();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Récupère la liste des slugs associés à un commerce par l'id du commerce.
   *
   * Cette fonction appelle le service afin d’obtenir
   * tous les slugs liés à un commerce donné.
   *
   * @async
   * @function getSlugsStoreById
   * @returns {Promise<AxiosResponse<ICommerceSlug>>}
   * Une Promise qui résout avec la réponse de l’API contenant
   * la liste des slugs, ou rejette avec une erreur en cas d’échec.
   */
  const getSlugsStoreById = async (): Promise<AxiosResponse<ICommerceSlug>> => {
    try {
      const response = await CommerceService.getSlugsStoreById();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Cette focniton gere la vérification de disponibilité du store .
   *
   * Cette fonction appelle le service afin d’obtenir
   * tous les slugs liés à un commerce donné.
   *
   * @async
   * @function checkAvailabilitySlugStore
   * @returns {Promise<AxiosResponse<any>>}
   * Une Promise qui résout avec la réponse de l’API contenant
   * la liste des slugs, ou rejette avec une erreur en cas d’échec.
   */
  const checkAvailabilitySlugStore = async (): Promise<AxiosResponse<any>> => {
    try {
      const response = await CommerceService.checkAvailabilitySlugStore();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Vérifie la disponibilité des slugs d’un store (commerce).
   *
   * Cette fonction appelle le service backend afin de récupérer
   * les suggestions de slugs associées à un commerce et d’indiquer
   * leur disponibilité.
   *
   * @async
   * @function checkSuggestionsSlugStore
   * @returns {Promise<AxiosResponse<IApiResponseCommerceSlugs>>}
   * Une Promise qui se résout avec la réponse de l’API contenant
   * les slugs proposés et leur statut de disponibilité,
   * ou qui se rejette avec une erreur en cas d’échec.
   */
  const checkSuggestionsSlugStore = async (): Promise<AxiosResponse<any>> => {
    try {
      const response = await CommerceService.checkSuggestionsSlugStore();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Supprime (désactive) un slug associé à un commerce.
   *
   * Cette méthode envoie une requête PUT à l’API afin de supprimer
   * logiquement un slug existant à partir de son identifiant.
   * La suppression peut correspondre à une désactivation plutôt
   * qu’à une suppression physique en base de données.
   *
   * @async
   * @function updateCommerceSlug
   *
   * @returns {Promise<import('axios').AxiosResponse | void>}
   * Retourne une Promise résolue avec la réponse Axios si la suppression réussit.
   * Retourne `void` si aucun identifiant de slug n’est fourni.
   * Rejette avec une `AxiosError` en cas d’échec de la requête.
   *
   * @throws {import('axios').AxiosError}
   * Levée si une erreur survient lors de l’appel API.
   */
  const updateCommerceSlug = async (data: any): Promise<AxiosResponse<any>> => {
    try {
      const response = await CommerceService.updateCommerceSlug(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Supprime un slug associé à un commerce.
   *
   * Cette fonction appelle le service backend afin de supprimer
   * un slug spécifique lié à un commerce donné.
   *
   * @async
   * @function deleteCommerceSlug
   * @returns {Promise<AxiosResponse<any>>}
   * Une Promise qui se résout avec la réponse de l’API confirmant
   * la suppression du slug,
   * ou qui se rejette avec une erreur en cas d’échec.
   */
  const deleteCommerceSlug = async (): Promise<AxiosResponse<any>> => {
    try {
      const response = await CommerceService.deleteCommerceSlug();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getSlugsByStoreId = async (): Promise<any> => {
    try {
      const response = await CommerceService.getSlugsByStoreId();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const addStoreContact = async (
    data: IAddStoreContactPayload,
  ): Promise<any> => {
    try {
      const response = await CommerceService.addStoreContact(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getStoreActivities = async (): Promise<any> => {
    try {
      const response = await CommerceService.getStoreActivities();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getStoreActivityById = async (id: string): Promise<any> => {
    try {
      const response = await CommerceService.getStoreActivityById(id);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getStoreById = async (storeId: string): Promise<any> => {
    try {
      const response = await CommerceService.getStoreById(storeId);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  return {
    createCommerceContact,
    createCommerce,
    createCommerceLogo,
    createCommerceSlug,
    dataGetCommerces,
    getCommerces,
    getCommerceSlugs,
    getSlugsStoreById,
    getSlugsByStoreId,
    UpdateCommerce,
    DeleteCommerce,
    deleteCommerceSlug,
    UpdateCommerceContact,
    DeleteCommerceContact,
    checkAvailabilitySlugStore,
    checkSuggestionsSlugStore,
    updateCommerceSlug,
    addStoreContact,
    getStoreActivities,
    getStoreActivityById,
    getStoreById,
  };
});
